import { HubConnectionBuilder, LogLevel } from '@microsoft/signalr';
import axios from 'axios';
import { getAuthHeader } from '../utils/jwtService';

class SignalRService {
  constructor() {
    this.connection = null;
    this.isConnected = false;
    this.connectionParams = null; // Store connection parameters for reconnection
    this.httpClient = null; // Store HTTP client for reconnection
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 5;
    this.pingInterval = null; // Add ping interval tracking
    this.connectionId = null; // Store connection ID
    this.transport = null; // Store transport type
    this.hubUrl = null; // Store hub URL
    this.connectionStartTime = null; // Track connection duration
    this.lastDisconnectDuration = null; // Track disconnect duration
    this.lastError = null; // Track last error

    // Check if proxy is being used (development with proxy in package.json)
    const useProxy = process.env.NODE_ENV === 'development' && !process.env.REACT_APP_FUNCTIONS_URL;

    if (useProxy) {
      // Use relative URL when proxy is configured
      this.functionsUrl = '/api/';
      console.log('Using proxy configuration for Azure Functions');
    } else if (process.env.REACT_APP_FUNCTIONS_URL) {
      // Use environment variable if set — normalize to always end with /
      // so raw fetch() string-concatenation works regardless of env var format
      this.functionsUrl = process.env.REACT_APP_FUNCTIONS_URL;
      if (!this.functionsUrl.endsWith('/')) this.functionsUrl += '/';
    } else {
      // Default to production URL
      this.functionsUrl = 'https://realtranslation-func-dev-eastus2.azurewebsites.net/api/';
    }

    console.log('SignalR Service Environment Configuration:', {
      functionsUrl: this.functionsUrl,
      NODE_ENV: process.env.NODE_ENV,
      REACT_APP_FUNCTIONS_URL: process.env.REACT_APP_FUNCTIONS_URL,
      useProxy: useProxy,
      hostname: window.location.hostname,
      origin: window.location.origin
    });
  }

  /**
   * Builds the Functions HTTP client used for negotiate / subscribe / add-language.
   *
   * Live Broadcast S3 (FR-24): AdminPortal creates its session with `fetchWithAuth` (bearer) but used to
   * rebuild a BARE Axios client for /subscribe, so it claimed `isHostMode: true` with no credential at
   * all. Once the shared subscribe boundary stopped trusting that flag, this host would have been
   * rejected. The token is attached by a REQUEST INTERCEPTOR rather than baked into `headers` at creation
   * time, because this client outlives a token refresh: the reconnect path re-uses the same instance, and
   * a header captured at connect time would be replayed after it had expired.
   *
   * @returns {import('axios').AxiosInstance} Configured Axios instance.
   */
  createHttpClient() {
    const client = axios.create({
      baseURL: this.functionsUrl,
      timeout: 60000 // Increased to 60 seconds for high load scenarios
    });

    client.interceptors.request.use((config) => {
      // Read the CURRENT token on every request. getAuthHeader() returns {} when signed out, which keeps
      // the anonymous participant flows working exactly as before.
      //
      // Axios normally hands the interceptor an AxiosHeaders instance, but a caller that builds its own
      // config (or a test passing a bare object) can leave `headers` undefined — and Object.assign throws
      // on undefined, which would fail the request outright rather than merely send it unauthenticated.
      // Initialize in place so an existing AxiosHeaders instance keeps its identity and methods.
      if (!config.headers) config.headers = {};
      Object.assign(config.headers, getAuthHeader());
      return config;
    });

    return client;
  }

  isLocalDevelopment() {
    return process.env.NODE_ENV === 'development' &&
      (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');
  }

  async connect(sessionId, targetLanguage, options = {}) {
    const {
      password,
      enableTts = false,
      showPartial = true,
      onTranslationReceived,
      onPartialTextReceived,
      onAudioReceived,
      onSessionCompleted,
      onSessionPaused,
      onSessionResumed,
      onSessionEnded, // New handler for explicit session end
      onSessionCanceled, // Handler for billing/policy termination with reason
      onSpeechActivity, // Handler for speech activity detection (server-side VAD)
      onConnectionStateChanged,
      isHostMode = false
    } = options;

    try {
      console.log('Starting SignalR connection...', {
        baseURL: this.functionsUrl,
        sessionId,
        targetLanguage,
        enableTts,
        showPartial,
        environment: process.env.NODE_ENV,
        hostname: window.location.hostname,
        protocol: window.location.protocol,
        userAgent: navigator.userAgent
      });

      // Carries the stored JWT on every Functions call (FR-24) and a 60s timeout for high load.
      this.httpClient = this.createHttpClient();

      // Store connection parameters for reconnection
      this.connectionParams = {
        sessionId,
        targetLanguage,
        password,
        enableTts,
        showPartial,
        onTranslationReceived,
        onPartialTextReceived,
        onAudioReceived,
        onSessionCompleted, // Note: This might be legacy, 'sessionEnded' is the new one
        onSessionEnded,
        onSessionPaused,
        onSessionResumed,
        onSpeechActivity,
        onConnectionStateChanged,
        isHostMode
      };

      // Negotiate SignalR connection (anonymous endpoint)
      console.log('Negotiating SignalR connection...', {
        baseURL: this.functionsUrl
      });
      const negotiateResponse = await this.httpClient.get('negotiate');
      console.log('Negotiate response status:', negotiateResponse.status);
      console.log('Negotiate response headers:', negotiateResponse.headers);
      console.log('Negotiate response data:', negotiateResponse.data);

      // Check if response is HTML instead of JSON (common with CORS/auth errors)
      const contentType = negotiateResponse.headers['content-type'] || '';
      if (contentType.includes('text/html')) {
        console.error('Received HTML response instead of JSON');
        throw new Error('Authentication failed or function key is incorrect. The server returned an HTML error page.');
      }

      // Check if response contains expected data
      if (!negotiateResponse.data || typeof negotiateResponse.data !== 'object') {
        console.error('Invalid response data type:', typeof negotiateResponse.data);
        throw new Error('Negotiate endpoint returned invalid data format');
      }

      // Handle nested response structure (similar to status endpoint)
      const responseData = negotiateResponse.data.Value || negotiateResponse.data;

      // Handle both camelCase and PascalCase property names
      const signalRUrl = responseData.url || responseData.Url;
      const accessToken = responseData.accessToken || responseData.AccessToken;

      console.log('Parsed negotiate response:', {
        signalRUrl,
        accessToken: accessToken ? 'Present' : 'Missing',
        rawData: negotiateResponse.data,
        responseData: responseData
      });

      if (!signalRUrl) {
        console.error('Invalid negotiate response - missing URL:', {
          data: negotiateResponse.data,
          responseData: responseData,
          keys: Object.keys(responseData || {}),
          url: responseData.url,
          Url: responseData.Url
        });
        throw new Error('SignalR URL not found in negotiate response. Function key might be incorrect or Azure Functions not properly configured');
      }

      // Store hub URL for debugging
      this.hubUrl = signalRUrl;

      // Build SignalR connection with improved timeout settings
      this.connection = new HubConnectionBuilder()
        .withUrl(signalRUrl, {
          accessTokenFactory: () => accessToken,
          // Add transport options to handle high load
          transport: 1, // WebSockets only for better performance
          skipNegotiation: false,
          // Add logging for transport selection
          logger: {
            log: (logLevel, message) => {
              if (logLevel >= LogLevel.Warning) {
                console.log('[SignalR Transport]', message);
              }
            }
          }
        })
        .configureLogging(LogLevel.Information)
        .withAutomaticReconnect({
          nextRetryDelayInMilliseconds: retryContext => {
            // Progressive backoff: 2s, 5s, 10s, 30s, 60s, then repeat 60s
            const delays = [2000, 5000, 10000, 30000, 60000];
            const delay = delays[Math.min(retryContext.previousRetryCount, delays.length - 1)];
            console.log(`[SignalR] Reconnect attempt ${retryContext.previousRetryCount + 1}, waiting ${delay}ms`);

            // Log more details about the disconnection
            if (retryContext.elapsedMilliseconds) {
              console.log(`[SignalR] Total time disconnected: ${Math.round(retryContext.elapsedMilliseconds / 1000)}s`);
            }

            return delay;
          }
        })
        // Add server timeout configuration aligned with infrastructure
        .withServerTimeout(90000) // 90 seconds server timeout - aligned with NGINX
        .withKeepAliveInterval(15000) // 15 seconds keep alive - more aggressive to detect disconnections faster
        .build();

      // Set up event handlers
      this.setupEventHandlers(sessionId, targetLanguage, {
        onTranslationReceived,
        onPartialTextReceived,
        onAudioReceived,
        onSessionCompleted,
        onSessionEnded,
        onSessionCanceled,
        onSessionPaused,
        onSessionResumed,
        onSpeechActivity,
        onConnectionStateChanged,
        onReconnecting: options.onReconnecting,
        onReconnected: options.onReconnected,
        onDisconnected: options.onDisconnected,
        onConnecting: options.onConnecting,
        onClose: options.onClose,
        onError: options.onError,
        showPartial,
        enableTts,
        isHostMode
      });

      // Start connection with retry logic
      await this.startConnectionWithRetry();
      this.isConnected = true;
      this.connectionStartTime = new Date();
      this.connectionId = this.connection.connectionId;
      // Properly get transport information after connection is established
      this.transport = this.connection.connection?.transport?.name ||
        this.connection._connectionState?.transport?.name ||
        'WebSockets';  // Default to WebSockets since we're forcing it

      console.log('[SignalR] Connection established:', {
        connectionId: this.connectionId,
        transport: this.transport,
        hubUrl: this.hubUrl,
        startTime: this.connectionStartTime.toISOString(),
        connectionState: this.connection.state
      });

      if (onConnectionStateChanged) {
        onConnectionStateChanged(true, 'Connected to SignalR');
      }

      // Subscribe to translation group
      await this.subscribeToSession(sessionId, targetLanguage, enableTts, password, isHostMode);

      // Start connection health monitoring
      this.startConnectionHealthMonitoring();

      return { success: true, message: `Connected to session ${sessionId}` };

    } catch (error) {
      this.isConnected = false;
      this.lastError = error;
      console.error('SignalR connection error:', error);
      console.error('Error details:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
        url: error.config?.url,
        baseURL: error.config?.baseURL,
        headers: error.config?.headers
      });

      // Additional debugging for common issues
      if (error.response?.status === 404) {
        console.error('404 Error - Azure Functions endpoint not found. Check if:', {
          functionsUrl: this.functionsUrl,
          suggestions: [
            'Azure Functions app is running',
            'Function key is correct',
            'URL path is correct (/api/negotiate)',
            'CORS is configured properly'
          ]
        });
      } else if (error.response?.status === 401) {
        console.error('401 Error - Authentication failed. This endpoint should be anonymous.');
      } else if (error.response?.status === 403) {
        console.error('403 Error - Access forbidden. Check CORS configuration');
      } else if (error.code === 'ECONNREFUSED' || error.code === 'ENOTFOUND') {
        console.error('Network error - Cannot reach Azure Functions:', {
          functionsUrl: this.functionsUrl,
          suggestions: [
            'Check network connectivity',
            'Verify Azure Functions URL is accessible',
            'Check firewall/proxy settings'
          ]
        });
      }

      if (onConnectionStateChanged) {
        onConnectionStateChanged(false, `Connection failed: ${error.message}`);
      }
      throw error;
    }
  }

  setupEventHandlers(sessionId, targetLanguage, handlers) {
    const {
      onTranslationReceived,
      onPartialTextReceived,
      onAudioReceived,
      onSessionCompleted,
      onSessionPaused,
      onSessionResumed,
      onConnectionStateChanged,
      onSessionEnded,
      onSessionCanceled,
      onSpeechActivity,
      showPartial,
      enableTts,
      isHostMode
    } = handlers;

    // Handle partial text (if enabled) - durationMs is 5th param, speaker is 6th param
    if (showPartial && onPartialTextReceived) {
      this.connection.on('ReceivePartialText', (
        sid, lang, text, tag, durationMs, speaker, utteranceId, lsmActive,
        sourceText, sourceUtteranceCompleted, routeGeneration
      ) => {
        // For host mode, accept translations for any language
        if (sid === sessionId && (isHostMode || lang === targetLanguage)) {
          onPartialTextReceived(text, tag, durationMs || 0, speaker || '', {
            sessionId: sid, language: lang, rawText: text, displayText: text, tag,
            durationMs: durationMs || 0, speaker: speaker || '', utteranceId: utteranceId || 0,
            lsmActive: lsmActive === true, sourceText: sourceText || '',
            sourceUtteranceCompleted: sourceUtteranceCompleted === true,
            routeGeneration: routeGeneration || 0, isFinal: false
          });
        }
      });
    }

    // Handle final translations (sourceText is 5th param, isDegraded is 6th param, durationMs is 7th param, speaker is 8th param)
    if (onTranslationReceived) {
      this.connection.on('ReceiveTranslation', (
        sid, lang, text, tag, sourceText, isDegraded, durationMs, speaker,
        glossaryMatches, utteranceId, routeGeneration
      ) => {
        // For host mode, accept translations for any language
        if (sid === sessionId && (isHostMode || lang === targetLanguage)) {
          onTranslationReceived(text, tag, sourceText, isDegraded, durationMs || 0, speaker || '', {
            sessionId: sid, language: lang, text, tag, sourceText: sourceText || '',
            isDegraded: isDegraded === true, durationMs: durationMs || 0,
            speaker: speaker || '', glossaryMatches: Array.isArray(glossaryMatches) ? glossaryMatches : [],
            utteranceId: utteranceId || 0, routeGeneration: routeGeneration || 0, isFinal: true
          });
        }
      });
    }

    // Handle audio chunks (if TTS enabled)
    if (enableTts && onAudioReceived) {
      console.log('Registering audio handler for TTS');

      // Register for audio events - the C# code sends raw byte array
      this.connection.on('ReceiveAudio', (audioData) => {
        console.log('Received audio chunk, size:', audioData?.length || 0);
        console.log('Audio data type:', typeof audioData);
        console.log('Audio data instanceof:', {
          Uint8Array: audioData instanceof Uint8Array,
          Array: Array.isArray(audioData),
          ArrayBuffer: audioData instanceof ArrayBuffer
        });

        if (audioData && audioData.length > 0) {
          let audioArray;

          // Handle different data formats from SignalR
          if (typeof audioData === 'string') {
            // If it's a base64 string, decode it
            console.log('Audio data is string, attempting base64 decode...');
            try {
              const binaryString = atob(audioData);
              audioArray = new Uint8Array(binaryString.length);
              for (let i = 0; i < binaryString.length; i++) {
                audioArray[i] = binaryString.charCodeAt(i);
              }
              console.log('Successfully decoded base64 audio, size:', audioArray.length);
            } catch (error) {
              console.error('Failed to decode base64 audio:', error);
              return;
            }
          } else if (audioData instanceof Uint8Array) {
            audioArray = audioData;
          } else if (Array.isArray(audioData)) {
            audioArray = new Uint8Array(audioData);
          } else if (audioData instanceof ArrayBuffer) {
            audioArray = new Uint8Array(audioData);
          } else {
            console.error('Unknown audio data format:', typeof audioData);
            return;
          }

          // Log first few bytes for debugging
          if (audioArray.length >= 16) {
            const firstBytes = Array.from(audioArray.slice(0, 16));
            console.log('First 16 bytes:', firstBytes.map(b => b.toString(16).padStart(2, '0')).join(' '));
          }

          console.log('Final audio array size:', audioArray.length);
          onAudioReceived(audioArray);
        }
      });
    }

    // Handle session completion
    if (onSessionCompleted) {
      this.connection.on('SessionCompleted', (sid) => {
        if (sid === sessionId) {
          onSessionCompleted();
        }
      });

      this.connection.on('SessionEnded', (sid, language) => {
        // Only trigger callback if it's for our subscribed language (or host mode which receives all)
        // This prevents showing "Session ended" when a different language's translation ends
        if (sid === sessionId && (isHostMode || language === targetLanguage)) {
          console.log('[SignalR] SessionEnded received for subscribed language:', { sid, language, targetLanguage });
          if (onSessionEnded) onSessionEnded();
          else if (onSessionCompleted) onSessionCompleted();
        } else if (sid === sessionId) {
          console.log('[SignalR] SessionEnded received for different language (ignored):', { sid, language, targetLanguage });
        }
      });

      // Register lowercase for serverless/Azure SignalR compatibility
      this.connection.on('sessionended', (sid, language) => {
        // Only trigger callback if it's for our subscribed language (or host mode which receives all)
        if (sid === sessionId && (isHostMode || language === targetLanguage)) {
          console.log('[SignalR] sessionended received for subscribed language:', { sid, language, targetLanguage });
          if (onSessionEnded) onSessionEnded();
          else if (onSessionCompleted) onSessionCompleted();
        } else if (sid === sessionId) {
          console.log('[SignalR] sessionended received for different language (ignored):', { sid, language, targetLanguage });
        }
      });
    }

    // Handle session pause/resume
    // Note: Azure SignalR in serverless mode may convert method names to lowercase
    // Register handlers for both PascalCase and lowercase to ensure compatibility
    if (onSessionPaused) {
      // Register for PascalCase
      this.connection.on('SessionPaused', (sid) => {
        if (sid === sessionId) {
          onSessionPaused();
        }
      });

      // Also register for lowercase (serverless mode compatibility)
      this.connection.on('sessionpaused', (sid) => {
        if (sid === sessionId) {
          onSessionPaused();
        }
      });
    }

    if (onSessionResumed) {
      // Register for PascalCase
      this.connection.on('SessionResumed', (sid) => {
        if (sid === sessionId) {
          onSessionResumed();
        }
      });

      // Also register for lowercase (serverless mode compatibility)
      this.connection.on('sessionresumed', (sid) => {
        if (sid === sessionId) {
          onSessionResumed();
        }
      });
    }

    // Handle session cancellation (billing/policy termination with reason)
    if (onSessionCanceled) {
      // Register for PascalCase
      this.connection.on('SessionCanceled', (sid, reason) => {
        if (sid === sessionId) {
          console.log('[SignalR] SessionCanceled received:', { sid, reason });
          onSessionCanceled(reason);
        }
      });

      // Also register for lowercase (serverless mode compatibility)
      this.connection.on('sessioncanceled', (sid, reason) => {
        if (sid === sessionId) {
          console.log('[SignalR] sessioncanceled received:', { sid, reason });
          onSessionCanceled(reason);
        }
      });
    }

    // Handle speech activity detection (server-side VAD signal)
    if (onSpeechActivity) {
      this.connection.on('SpeechActivity', (sid, isSpeaking) => {
        if (sid === sessionId) {
          onSpeechActivity(isSpeaking);
        }
      });

      // Lowercase variant for Azure SignalR serverless compatibility
      this.connection.on('speechactivity', (sid, isSpeaking) => {
        if (sid === sessionId) {
          onSpeechActivity(isSpeaking);
        }
      });
    }

    // Handle connection state changes
    this.connection.onclose((error) => {
      this.isConnected = false;
      this.clearHealthMonitoring();

      // Calculate connection duration
      if (this.connectionStartTime) {
        const duration = new Date() - this.connectionStartTime;
        this.lastDisconnectDuration = duration;
        console.log(`[SignalR] Connection duration: ${Math.round(duration / 1000)}s`);
      }

      this.lastError = error;

      // Enhanced error logging
      console.error('[SignalR] Connection closed:', {
        error: error?.message || error,
        errorType: error?.constructor?.name,
        connectionId: this.connectionId,
        transport: this.transport,
        duration: this.lastDisconnectDuration,
        timestamp: new Date().toISOString()
      });

      if (onConnectionStateChanged) {
        const message = error ? `Connection closed: ${error.message}` : 'Connection closed';
        onConnectionStateChanged(false, message);
      }

      // Call custom handlers
      if (handlers.onDisconnected) {
        handlers.onDisconnected(error);
      }
    });

    this.connection.onreconnecting((error) => {
      this.reconnectAttempts++;

      console.warn('[SignalR] Reconnecting:', {
        attempt: this.reconnectAttempts,
        error: error?.message || error,
        lastError: this.lastError?.message,
        lastDisconnectDuration: this.lastDisconnectDuration,
        transport: this.transport,
        timestamp: new Date().toISOString()
      });

      if (onConnectionStateChanged) {
        onConnectionStateChanged(false, `Reconnecting... (attempt ${this.reconnectAttempts})`);
      }

      // Call custom handlers
      if (handlers.onReconnecting) {
        handlers.onReconnecting(error);
      }
    });

    this.connection.onreconnected(async (connectionId) => {
      this.connectionId = connectionId;
      this.connectionStartTime = new Date();
      // Update transport info after reconnection
      this.transport = this.connection.connection?.transport?.name ||
        this.connection._connectionState?.transport?.name ||
        'WebSockets';

      console.log('[SignalR] Reconnected successfully:', {
        newConnectionId: connectionId,
        previousDisconnectDuration: this.lastDisconnectDuration,
        reconnectAttempts: this.reconnectAttempts,
        transport: this.transport,
        hasHttpClient: !!this.httpClient,
        hasConnectionParams: !!this.connectionParams,
        timestamp: new Date().toISOString()
      });

      this.isConnected = true;
      this.reconnectAttempts = 0;

      try {
        // Safety check: Recreate httpClient if it was destroyed (shouldn't happen with the fix above)
        if (!this.httpClient && this.connectionParams) {
          console.log('[SignalR] Recreating httpClient after reconnection');
          this.httpClient = this.createHttpClient();
        }

        await this.reestablishConnection(connectionId);
        this.startConnectionHealthMonitoring(); // Restart health monitoring

        if (onConnectionStateChanged) {
          onConnectionStateChanged(true, 'Reconnected and re-subscribed');
        }

        // Call custom handlers
        if (handlers.onReconnected) {
          handlers.onReconnected();
        }
      } catch (error) {
        console.error('[SignalR] Failed to re-establish connection after reconnect:', {
          error: error.message,
          connectionId,
          timestamp: new Date().toISOString()
        });
        this.isConnected = false;

        if (onConnectionStateChanged) {
          onConnectionStateChanged(false, `Reconnect failed: ${error.message}`);
        }
      }
    });
  }

  async reestablishConnection(connectionId) {
    console.log('[SignalR] Attempting reestablishment:', {
      hasHttpClient: !!this.httpClient,
      hasConnectionParams: !!this.connectionParams,
      connectionId
    });

    if (!this.connectionParams || !this.httpClient) {
      throw new Error('Connection parameters not available for reconnection');
    }

    const { sessionId, targetLanguage, enableTts, password, isHostMode } = this.connectionParams;

    // The reconnect path receives the NEW connection id but used to leave `this.connectionId`
    // holding the old one — only `onreconnected` ever assigned it. That was invisible until the
    // presence beat started carrying the id (#2103): after a reconnect the beat would name a
    // connection the server no longer has, get refused, and the host would be reaped as stale —
    // #2094 through a different door.
    this.connectionId = connectionId;

    console.log('Re-establishing connection with new connection ID:', connectionId);

    // Re-subscribe to translation groups
    const subscribeData = {
      connectionId: connectionId,
      sessionId: sessionId,
      language: targetLanguage,
      password: password,
      text: true,
      audio: enableTts,
      isHostMode: isHostMode
    };

    try {
      await this.httpClient.post('subscribe', subscribeData);
      console.log('Successfully re-subscribed to groups after reconnection');

      // Verify connection by adding language again (idempotent operation)
      await this.httpClient.post(`translation/add-language?sessionId=${encodeURIComponent(sessionId)}&targetLanguage=${encodeURIComponent(targetLanguage)}`, {});
      console.log('Successfully re-added language to session after reconnection');

      // Re-arm against the NEW registration — a reconnect re-registers this host.
      if (isHostMode) {
        this.startPresenceHeartbeat(sessionId);
      }

    } catch (error) {
      console.error('Failed to re-establish connection:', error);
      throw error;
    }
  }

  async verifyConnection() {
    if (!this.connection || !this.isConnected) {
      return false;
    }

    try {
      // Send a test message to verify the connection is working
      // This is a simple way to check if the connection is truly functional
      const state = this.connection.state;
      console.log('Connection state:', state);
      return state === 'Connected';
    } catch (error) {
      console.error('Connection verification failed:', error);
      return false;
    }
  }

  async disconnect(intentional = true) {
    this.clearHealthMonitoring();

    if (this.connection) {
      console.log('[SignalR] Disconnect called:', {
        intentional,
        hadConnection: !!this.connection,
        hadHttpClient: !!this.httpClient,
        connectionState: this.connection.state,
        connectionId: this.connectionId,
        transport: this.transport,
        timestamp: new Date().toISOString()
      });

      try {
        // Only try to stop if we're actually connected
        if (this.connection.state !== 'Disconnected') {
          await this.connection.stop();
        }
      } catch (error) {
        console.error('Error stopping SignalR connection:', error);
      }

      this.connection = null;
      this.isConnected = false;
      this.connectionId = null;
      this.transport = null;

      // Only destroy httpClient and connectionParams if this is an intentional disconnect
      // Preserve for automatic reconnections
      if (intentional) {
        this.connectionParams = null;
        this.httpClient = null;
        this.reconnectAttempts = 0;
        this.lastError = null;
        this.lastDisconnectDuration = null;
        console.log('[SignalR] Intentional disconnect - destroyed connection state');
      } else {
        console.log('[SignalR] Auto-reconnect disconnect - preserved httpClient for reconnection');
      }
    }
  }

  getConnectionState() {
    return this.connection ? this.connection.state : 'Disconnected';
  }

  get connectionDuration() {
    if (!this.connectionStartTime || !this.isConnected) {
      return null;
    }
    return new Date() - this.connectionStartTime;
  }

  async startConnectionWithRetry(maxAttempts = 3) {
    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
      try {
        console.log(`[SignalR] Connection attempt ${attempt}/${maxAttempts}`);

        // Call custom handler
        if (this.connectionParams?.onConnecting) {
          this.connectionParams.onConnecting();
        }

        await this.connection.start();
        this.isConnected = true;
        this.reconnectAttempts = 0;
        console.log('[SignalR] Connection established successfully');
        return;
      } catch (error) {
        console.error(`[SignalR] Connection attempt ${attempt} failed:`, {
          error: error.message,
          errorType: error.constructor.name,
          attempt,
          maxAttempts,
          timestamp: new Date().toISOString()
        });

        this.lastError = error;

        if (attempt === maxAttempts) {
          throw error;
        }

        // Wait before retry with exponential backoff
        const delay = Math.pow(2, attempt) * 1000;
        console.log(`[SignalR] Waiting ${delay}ms before retry...`);
        await new Promise(resolve => setTimeout(resolve, delay));
      }
    }
  }

  /**
   * Starts the 30s presence heartbeat for a HOST connection.
   *
   * AdminPortal subscribes as an authenticated host but sent no heartbeat at all, so its host row's
   * LastHeartbeat froze at JoinedAt and the backend's stale sweep reaped it ~60s in
   * (`PresenceCleanupFunction.cs:32`). Presence is keyed on the canonical `host:{sessionId}` the
   * subscribe boundary assigns every granted host (`SignalRSubscriberFunction.cs:290`) — see #2094.
   *
   * Receiver-mode connections send nothing: this surface has no participant token of its own and
   * must not invent one.
   *
   * @param {string} sessionId The session to beat for.
   */
  startPresenceHeartbeat(sessionId) {
    this.stopPresenceHeartbeat();

    const sendHeartbeat = async () => {
      if (!this.httpClient || !this.connectionParams?.isHostMode) {
        return;
      }

      try {
        // `connectionId` is REQUIRED (#2103) — `host:${sessionId}` is derivable from the shareable
        // session code, so the beat must also name the server-assigned connection it comes from.
        await this.httpClient.post('presence/heartbeat', {
          sessionId,
          userToken: `host:${sessionId}`,
          connectionId: this.connectionId
        });
      } catch (error) {
        // Best-effort, matching ZoomClient: one missed beat is recoverable inside the ~60s window.
        console.warn('[SignalR] Presence heartbeat failed:', error.message);
      }
    };

    sendHeartbeat();
    this.heartbeatInterval = setInterval(sendHeartbeat, 30000);
  }

  /** Stops the presence heartbeat. Safe to call when nothing is armed. */
  stopPresenceHeartbeat() {
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
      this.heartbeatInterval = null;
    }
  }

  async subscribeToSession(sessionId, targetLanguage, enableTts, password, isHostMode = false) {
    const subscribeData = {
      connectionId: this.connection.connectionId,
      sessionId: sessionId,
      language: targetLanguage,
      password: password,
      text: true,
      audio: enableTts,
      isHostMode: isHostMode
    };

    try {
      await this.httpClient.post('subscribe', subscribeData);
      console.log('Successfully subscribed to session');
    } catch (subscribeError) {
      // Add more context to subscription errors
      if (subscribeError.response?.status === 404) {
        throw new Error(`Session "${sessionId}" does not exist or is not active`);
      }
      throw subscribeError;
    }

    // Add language to session
    await this.httpClient.post(`translation/add-language?sessionId=${encodeURIComponent(sessionId)}&targetLanguage=${encodeURIComponent(targetLanguage)}`, {});

    // Only a REGISTERED host is tracked, so the beat starts after the subscribe succeeds.
    if (isHostMode) {
      this.startPresenceHeartbeat(sessionId);
    }
  }

  startConnectionHealthMonitoring() {
    // Clear any existing ping interval
    if (this.pingInterval) {
      clearInterval(this.pingInterval);
    }

    // Monitor connection state instead of sending pings
    // SignalR has built-in keep-alive mechanisms, so we just monitor the state
    this.pingInterval = setInterval(() => {
      if (this.connection) {
        const state = this.getConnectionState();
        const isHealthy = state === 'Connected';

        // Log connection state periodically (every 5th check to reduce noise)
        if (Math.random() < 0.2) {
          console.log('[SignalR] Connection health check:', {
            state,
            isHealthy,
            connectionId: this.connectionId,
            duration: this.connectionDuration ? `${Math.round(this.connectionDuration / 1000)}s` : 'N/A',
            timestamp: new Date().toISOString()
          });
        }

        // If connection is in a bad state, log a warning
        if (!isHealthy && this.isConnected) {
          console.warn('[SignalR] Connection unhealthy:', {
            state,
            lastError: this.lastError?.message,
            timestamp: new Date().toISOString()
          });
          // The automatic reconnect will handle recovery
        }
      }
    }, 30000); // Check every 30 seconds
  }

  clearHealthMonitoring() {
    if (this.pingInterval) {
      clearInterval(this.pingInterval);
      this.pingInterval = null;
    }
    // The presence beat is torn down with the rest of the connection's timers — both the
    // intentional disconnect and the close handler route through here.
    this.stopPresenceHeartbeat();
  }

  // Manual test function to verify Azure Functions connectivity
  // Note: negotiate endpoint is anonymous, no auth required
  async testAzureFunctionsConnectivity() {
    try {
      console.log('Testing Azure Functions connectivity...', {
        url: this.functionsUrl
      });

      const testClient = axios.create({
        baseURL: this.functionsUrl,
        timeout: 10000
      });

      const response = await testClient.get('negotiate');
      console.log('Azure Functions test - SUCCESS:', {
        status: response.status,
        headers: response.headers,
        data: response.data
      });
      return true;
    } catch (error) {
      console.error('Azure Functions test - FAILED:', {
        message: error.message,
        status: error.response?.status,
        data: error.response?.data,
        url: error.config?.url
      });
      return false;
    }
  }
}

const signalRServiceInstance = new SignalRService();
export default signalRServiceInstance;
